// AccountDrawer — the Accounts-page HUB detail panel.
//
// A right-anchored Drawer (reusing @/components/nexus/Drawer) that consolidates,
// for ONE pipeline account, everything today scattered across columns, dialogs,
// and the separate BC dashboard:
//
//   Identity     — advertiser id, geo, platform, created; placeholder badge.
//   Attribution  — owning client + agency; Reattribute / Unlink (the shared
//                  AttributionUnlinkControls, incl. the append_allowlist opt-in).
//   Status       — the A7 status badge + "Change status…" (reuses the page's
//                  StatusDialog via onChangeStatus).
//   Access       — the PROXY-allowlist membership (row.in_client_allowlist) with
//                  the explicit Add / Remove affordances, and — clearly SEPARATE
//                  — the provisioning-execution flag (accounts.whitelist).
//   Live health  — lazily GET /admin/dashboard/accounts/{account_id} for the BC
//                  balance / spend / sync-health. Fail-SOFT: an absent/failed BC
//                  row shows "balance unavailable" and NEVER blocks the drawer.
//   Linked req.  — a deep-link to the SPECIFIC request.
//   Footer       — open the full BC detail page.
//
// SECURITY: everything here is either in-hand row metadata or sanitized TikTok
// RESPONSE data the backend returns — no master token, no client secrets. The
// only writes are the EXPLICIT, audited allowlist add/remove + attribution/
// unlink + provisioning toggle, each resolving the owning client SERVER-SIDE
// from the row (invariants 2/3/4).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  addToAllowlist,
  deallowlistRow,
  getAccountBCDetail,
  isUnboundClient,
  setAccountWhitelist,
  statusNeedsReason,
  type AccountRow,
  type BCAccountDetail,
} from "@/api/accounts";
import { errMessage } from "@/lib/errMessage";
import {
  AttributionUnlinkControls,
  Drawer,
  ToneBadge,
  type Tone,
} from "@/components/nexus";
import { financeView } from "@/api/financeview";
import {
  AccountStatusChip,
  FinanceViewBadge,
  StaleBanner,
  balanceIssueReason,
  balanceUnavailable,
  financeViewLine,
  syncedLabel,
} from "@/routes/DashboardPage";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface Ref {
  id: string;
  name: string;
}

function statusTone(status: string): Tone {
  switch (status) {
    case "active":
      return "credit";
    case "whitelisted":
      return "brand";
    case "paused":
      return "warn";
    case "banned":
      return "danger";
    default:
      return "na";
  }
}

function fmtCreated(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

// One label/value line.
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-nx-border/60 py-1.5 last:border-0">
      <span className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
        {label}
      </span>
      <span className="text-right text-sm text-nx-text">{children}</span>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-1">
      <h3 className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function AccountDrawer({
  row,
  agencies,
  clientName,
  agencyName,
  onOpenChange,
  onRowUpdated,
  onChangeStatus,
}: {
  row: AccountRow | null;
  agencies: Ref[];
  clientName: (id: string) => string;
  agencyName: (id: string) => string;
  onOpenChange: (open: boolean) => void;
  onRowUpdated: (row: AccountRow) => void;
  onChangeStatus: (row: AccountRow) => void;
}) {
  return (
    <Drawer
      open={row !== null}
      onOpenChange={onOpenChange}
      title={
        row ? (
          <span className="font-mono">
            {row.account_id || "Pending account"}
          </span>
        ) : undefined
      }
      description={row ? clientName(row.client_id) : undefined}
    >
      {row && (
        // Keying by row id resets all per-account state (lazy balance fetch,
        // busy flags, attribution mode) when a different row is opened.
        <AccountDrawerBody
          key={row.id}
          row={row}
          agencies={agencies}
          clientName={clientName}
          agencyName={agencyName}
          onRowUpdated={onRowUpdated}
          onChangeStatus={onChangeStatus}
        />
      )}
    </Drawer>
  );
}

function AccountDrawerBody({
  row,
  agencies,
  clientName,
  agencyName,
  onRowUpdated,
  onChangeStatus,
}: {
  row: AccountRow;
  agencies: Ref[];
  clientName: (id: string) => string;
  agencyName: (id: string) => string;
  onRowUpdated: (row: AccountRow) => void;
  onChangeStatus: (row: AccountRow) => void;
}) {
  const [allowBusy, setAllowBusy] = useState(false);
  const [provBusy, setProvBusy] = useState(false);

  // Lazy BC balance/health — fetched ONLY for a row with a real advertiser id.
  const [bc, setBc] = useState<BCAccountDetail | null>(null);
  const [bcLoading, setBcLoading] = useState(false);
  const [bcFailed, setBcFailed] = useState(false);

  useEffect(() => {
    if (!row.account_id) return;
    let cancelled = false;
    setBcLoading(true);
    setBcFailed(false);
    getAccountBCDetail(row.account_id)
      .then((res) => {
        if (!cancelled) setBc(res);
      })
      .catch(() => {
        // Fail SOFT — the drawer stays fully usable without the BC row.
        if (!cancelled) setBcFailed(true);
      })
      .finally(() => {
        if (!cancelled) setBcLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row.account_id]);

  // Explicit, audited PROXY-allowlist merge — the owning client is resolved
  // server-side from the row (409 unbound_client when there is none).
  async function addAccess() {
    setAllowBusy(true);
    try {
      const res = await addToAllowlist(row.id);
      onRowUpdated({ ...row, in_client_allowlist: res.in_client_allowlist });
      toast.success("Added to the client's proxy allowlist");
    } catch (err) {
      if (isUnboundClient(err)) {
        toast.error("Attribute this account to a client first.");
      } else {
        toast.error(errMessage(err));
      }
    } finally {
      setAllowBusy(false);
    }
  }

  // The EXPLICIT removal — never auto-triggered.
  async function removeAccess() {
    setAllowBusy(true);
    try {
      const res = await deallowlistRow(row.id);
      onRowUpdated({ ...row, in_client_allowlist: res.in_client_allowlist });
      toast.success("Removed from the client's proxy allowlist");
    } catch (err) {
      if (isUnboundClient(err)) {
        toast.error("This account has no owning client.");
      } else {
        toast.error(errMessage(err));
      }
    } finally {
      setAllowBusy(false);
    }
  }

  // Provisioning-execution flag (accounts.whitelist) — NOT proxy access.
  async function toggleProvisioning() {
    setProvBusy(true);
    try {
      const res = await setAccountWhitelist(row.id, !row.whitelist);
      onRowUpdated({ ...row, whitelist: res.whitelist });
      toast.success(
        res.whitelist ? "Marked provisioned" : "Provisioning cleared",
      );
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setProvBusy(false);
    }
  }

  const accessApplies = row.client_id !== "" && row.account_id !== "";

  return (
    <div className="grid gap-5">
      {/* Identity */}
      <Section title="Identity">
        <Field label="Ads account #">
          {row.account_id ? (
            <span className="font-mono tabular-nums">{row.account_id}</span>
          ) : (
            <span className="text-nx-text-3">— pending</span>
          )}
        </Field>
        <Field label="Geo">
          <span className="font-mono">{row.geo || "—"}</span>
        </Field>
        <Field label="Platform">{row.platform || "—"}</Field>
        <Field label="Created">{fmtCreated(row.created_at)}</Field>
        {row.is_placeholder && (
          <div className="pt-1">
            <ToneBadge tone="warn">Legacy placeholder — no real account</ToneBadge>
          </div>
        )}
      </Section>

      {/* Attribution */}
      <Section title="Attribution">
        <Field label="Client">{clientName(row.client_id)}</Field>
        <Field label="Agency">{agencyName(row.agency_id)}</Field>
        <AttributionUnlinkControls
          account={row}
          agencies={agencies}
          agencyName={agencyName}
          onUpdated={onRowUpdated}
          onUnlinked={onRowUpdated}
        />
      </Section>

      {/* Status */}
      <Section title="Status">
        <div className="flex items-center justify-between gap-3">
          <div className="grid gap-0.5">
            <ToneBadge
              tone={statusTone(row.status_account)}
              className="w-fit capitalize"
            >
              {row.status_account || "—"}
            </ToneBadge>
            {statusNeedsReason(row.status_account) && row.status_reason && (
              <span className="text-xs text-nx-text-3">{row.status_reason}</span>
            )}
          </div>
          <Button
            variant="outline"
            size="xs"
            onClick={() => onChangeStatus(row)}
          >
            Change status…
          </Button>
        </div>
      </Section>

      {/* Access — the PROXY allowlist gate + the separate provisioning flag */}
      <Section title="Access">
        {!accessApplies ? (
          <p className="text-xs text-nx-text-3">
            Proxy access applies only to a delivered account with an owning
            client.
          </p>
        ) : row.in_client_allowlist ? (
          <div className="flex items-center justify-between gap-3">
            <ToneBadge tone="credit" className="w-fit">
              In allowlist ✓
            </ToneBadge>
            <Button
              variant="outline"
              size="xs"
              disabled={allowBusy}
              aria-label={`Remove ${row.id} from allowlist`}
              onClick={removeAccess}
            >
              {allowBusy ? "Removing…" : "Remove access"}
            </Button>
          </div>
        ) : (
          <div className="grid gap-1.5">
            <span role="alert" className="text-xs font-medium text-nx-danger">
              NOT in allowlist ⚠ — client can&apos;t see or recharge this account
            </span>
            <Button
              variant="outline"
              size="xs"
              className="w-fit"
              disabled={allowBusy}
              aria-label={`Add ${row.id} to allowlist`}
              onClick={addAccess}
            >
              {allowBusy ? "Adding…" : "Add to allowlist"}
            </Button>
          </div>
        )}

        {/* Provisioning-execution status — DELIBERATELY separate from proxy
            access; this only tracks whether ops has provisioned the account. */}
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-nx-border/60 pt-2">
          <div className="grid gap-0.5">
            <span className="text-xs font-medium text-nx-text-2">
              Provisioning{" "}
              <span className="font-normal text-nx-text-3">
                (execution status — not access)
              </span>
            </span>
            {row.whitelist ? (
              <ToneBadge tone="brand" className="w-fit">
                Provisioned
              </ToneBadge>
            ) : (
              <span className="text-xs text-nx-text-3">Not provisioned</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="xs"
            disabled={provBusy}
            aria-label={`Provisioning ${row.whitelist ? "off" : "on"} ${row.id}`}
            onClick={toggleProvisioning}
          >
            {row.whitelist ? "Remove" : "Add"}
          </Button>
        </div>
      </Section>

      {/* Live balance & health (lazy; fail-soft) */}
      {row.account_id !== "" && (
        <Section title="Live balance & health">
          {bcLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : bcFailed || !bc ? (
            <div className="grid gap-0.5">
              <ToneBadge tone="warn" className="w-fit">
                balance unavailable
              </ToneBadge>
              <span className="text-xs text-nx-text-3">
                the BC cache has no row for this account yet
              </span>
            </div>
          ) : (
            <div className="grid gap-2">
              {bc.stale && <StaleBanner />}
              <div className="flex items-center gap-2">
                <AccountStatusChip
                  displayStatus={bc.account.display_status}
                />
                <span className="text-xs text-nx-text-3">
                  {syncedLabel(bc.account.synced_at)}
                </span>
              </div>
              <Field label="Balance">
                {balanceUnavailable(bc.account.balance) ? (
                  <span className="grid justify-items-end gap-0.5">
                    <ToneBadge tone="warn" className="w-fit">
                      balance unavailable
                    </ToneBadge>
                    <span className="text-xs text-nx-text-3">
                      {balanceIssueReason(bc.stale)}
                    </span>
                  </span>
                ) : (
                  <span className="font-mono tabular-nums">
                    {bc.account.balance} {bc.account.currency}
                  </span>
                )}
              </Field>
              {/* WHY the balance above is (un)readable: the ADR-0054 finance
                  bindings the detail fetch now carries. Absent on an older
                  backend -> render nothing, never "no fin view". */}
              {financeView(bc.account).known && (
                <Field label="Financial view">
                  <span className="grid justify-items-end gap-0.5">
                    <FinanceViewBadge account={bc.account} />
                    <span className="text-xs text-nx-text-3">
                      {financeViewLine(bc.account)}
                    </span>
                  </span>
                </Field>
              )}
              <Field label="Spend · 7d">
                <span className="font-mono tabular-nums">
                  {bc.spend["7d"]?.spend ?? "0"} {bc.account.currency}
                </span>
              </Field>
              <Field label="Spend · 30d">
                <span className="font-mono tabular-nums">
                  {bc.spend["30d"]?.spend ?? "0"} {bc.account.currency}
                </span>
              </Field>
            </div>
          )}
        </Section>
      )}

      {/* Linked request — deep-link the SPECIFIC request */}
      <Section title="Linked request">
        {row.request_id ? (
          <Link
            to={`/account-requests?request=${row.request_id}`}
            title={row.request_id}
            className="w-fit font-mono text-xs text-nx-brand underline-offset-2 hover:underline"
          >
            {row.request_id.slice(0, 8)} →
          </Link>
        ) : (
          <span className="text-xs text-nx-text-3">
            Not linked to a request.
          </span>
        )}
      </Section>

      {/* Footer */}
      {row.account_id !== "" && (
        <div className="border-t border-nx-border pt-3">
          <Link
            to={`/dashboard/accounts/${row.account_id}`}
            className="text-sm text-nx-brand underline-offset-4 hover:underline"
          >
            Open full BC detail →
          </Link>
        </div>
      )}
    </div>
  );
}
